import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type { ClubAPI } from "@shared/types/api";

// Clubs: themed communities with a forum, member-only polls and per-city
// in-person events. The server returns `access.can` for the club page, so
// gating decisions are never re-derived on the client.

export const clubKeys = {
  all: ["clubs"] as const,
  detail: (slug: string) => ["clubs", slug] as const,
  posts: (slug: string) => ["clubs", slug, "posts"] as const,
  post: (slug: string, postId: string) => ["clubs", slug, "posts", postId] as const,
};

/** Gate code on a 403 from a club endpoint, if any. */
export function clubGateCode(error: unknown): "membership_required" | "subscription_required" | undefined {
  return (error as { code?: any } | null)?.code;
}

function unwrap<T>(json: { success: boolean; data?: T; error?: string }): T {
  if (!json.success || json.data === undefined) throw new Error(json.error || "Request failed");
  return json.data;
}

export function useClubs() {
  return useQuery({
    queryKey: clubKeys.all,
    queryFn: async () => unwrap(await api.get<ClubAPI.ClubSummary[]>("/user/clubs")),
  });
}

export function useClub(slug: string | undefined) {
  return useQuery({
    queryKey: clubKeys.detail(slug ?? ""),
    enabled: !!slug,
    queryFn: async () => unwrap(await api.get<ClubAPI.ClubDetail>(`/user/clubs/${slug}`)),
  });
}

export function useClubPosts(slug: string | undefined, enabled = true) {
  return useInfiniteQuery({
    queryKey: clubKeys.posts(slug ?? ""),
    enabled: !!slug && enabled,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const qs = pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : "";
      return unwrap(await api.get<ClubAPI.ClubPostPage>(`/user/clubs/${slug}/posts${qs}`));
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useClubPost(slug: string | undefined, postId: string | undefined) {
  return useQuery({
    queryKey: clubKeys.post(slug ?? "", postId ?? ""),
    enabled: !!slug && !!postId,
    queryFn: async () => unwrap(await api.get<ClubAPI.ClubPostThread>(`/user/clubs/${slug}/posts/${postId}`)),
  });
}

export function useJoinClub(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (join: boolean) => {
      const json = join
        ? await api.post(`/user/clubs/${slug}/join`)
        : await api.delete(`/user/clubs/${slug}/join`);
      if (!json.success) throw new Error(json.error || "Request failed");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clubKeys.all }),
  });
}

export function useCreateClubPost(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ClubAPI.CreatePostRequest) =>
      unwrap(await api.post<ClubAPI.ClubPost>(`/user/clubs/${slug}/posts`, data)),
    onSuccess: (post) => {
      queryClient.invalidateQueries({ queryKey: clubKeys.posts(slug) });
      if (post.parentId) queryClient.invalidateQueries({ queryKey: clubKeys.post(slug, post.parentId) });
    },
  });
}

export function useDeleteClubPost(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (postId: string) => {
      const json = await api.delete(`/user/clubs/${slug}/posts/${postId}`);
      if (!json.success) throw new Error(json.error || "Failed to delete post");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clubKeys.posts(slug) }),
  });
}

export function useReportClubPost(slug: string) {
  return useMutation({
    mutationFn: async ({ postId, ...data }: ClubAPI.ReportPostRequest & { postId: string }) => {
      const json = await api.post(`/user/clubs/${slug}/posts/${postId}/report`, data);
      if (!json.success) throw new Error(json.error || "Failed to report post");
    },
  });
}
